import { Component } from "@theme/component";
import { prefersReducedMotion } from "@theme/utilities";

class BrandVideoTestimonials extends Component {
  static get observedAttributes() {
    return ["data-autoplay"];
  }

  requiredRefs = ["track"];

  // Global mute state shared across instances
  static isGlobalMuted = true;

  constructor() {
    super();
    this.isDragging = false;
    this.startX = 0;
    this.scrollLeftStart = 0;
    this.scrollTimeout = null;
    this.activeSlideIndex = 0;
    this.autoplayTimer = null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.initializeSlider();
    this.setupDragging();
    this.setupCardControls();

    // Add slide class updates initially
    this.updateControlsState();

    this.startAutoplay();
    this.setupAutoplayEvents();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    super.disconnectedCallback();
  }

  get slides() {
    return Array.from(this.refs.track?.querySelectorAll(".brand-video-testimonials__slide") || []);
  }

  get dots() {
    if (!this.refs.dots) return [];
    return Array.from(this.refs.dots.querySelectorAll(".brand-video-testimonials__dot"));
  }

  /**
   * Setup active slide dots and arrow click handlers
   */
  initializeSlider() {
    const { prev, next, track } = this.refs;

    // Dot navigation
    this.dots.forEach((dot, index) => {
      dot.addEventListener("click", () => {
        this.scrollToSlide(index);
        this.startAutoplay();
      });
    });

    // Arrow navigation
    if (prev) {
      prev.addEventListener("click", () => {
        const isLoop = this.getAttribute("data-slider-loop") === "true";
        let nextIndex = this.activeSlideIndex - 1;
        if (!isLoop) {
          nextIndex = Math.max(0, nextIndex);
        }
        this.scrollToSlide(nextIndex);
        this.startAutoplay();
      });
    }

    if (next) {
      next.addEventListener("click", () => {
        const isLoop = this.getAttribute("data-slider-loop") === "true";
        let nextIndex = this.activeSlideIndex + 1;
        if (!isLoop) {
          nextIndex = Math.min(this.slides.length - 1, nextIndex);
        }
        this.scrollToSlide(nextIndex);
        this.startAutoplay();
      });
    }

    // Scroll listener to update dots and arrows state dynamically
    if (track) {
      track.addEventListener("scroll", this.handleScroll.bind(this), { passive: true });
    }
  }

  /**
   * Handle scroll event to update active slide dots and navigation arrows
   */
  handleScroll() {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    // Throttled update to prevent performance degradation
    this.scrollTimeout = setTimeout(() => {
      const { track } = this.refs;
      if (!track) return;
      if (track.querySelector(".is-clone")) return;

      const trackScrollLeft = track.scrollLeft;

      // Find slide closest to the scroll viewport start
      let closestIndex = 0;
      let minDistance = Infinity;

      this.slides.forEach((slide, index) => {
        const distance = Math.abs(slide.offsetLeft - track.offsetLeft - trackScrollLeft);
        if (distance < minDistance) {
          minDistance = distance;
          closestIndex = index;
        }
      });

      if (closestIndex !== this.activeSlideIndex) {
        this.activeSlideIndex = closestIndex;
        this.updateControlsState();
      }
    }, 100);
  }

  /**
    * Scroll smoothly to a specific slide by index
    */
  scrollToSlide(index) {
    const { track } = this.refs;
    const slides = this.slides;
    const slideCount = slides.length;
    if (!track || slideCount === 0) return;

    const isLoop = this.getAttribute("data-slider-loop") === "true";

    // Loop forward: transition to a clone of slide 0 at the end
    if (isLoop && index >= slideCount) {
      const firstSlide = slides[0];
      const clone = firstSlide.cloneNode(true);
      clone.classList.add("is-clone");
      track.appendChild(clone);

      const targetScrollLeft = clone.offsetLeft - track.offsetLeft;
      track.scrollTo({
        left: targetScrollLeft,
        behavior: "smooth"
      });

      this.activeSlideIndex = 0;
      this.updateControlsState();

      setTimeout(() => {
        track.style.scrollBehavior = "auto";
        track.scrollLeft = firstSlide.offsetLeft - track.offsetLeft;
        track.style.scrollBehavior = "smooth";
        clone.remove();
      }, 500);
      return;
    }

    // Loop backward: transition to a clone of the last slide at the start
    if (isLoop && index < 0) {
      const lastSlide = slides[slideCount - 1];
      const clone = lastSlide.cloneNode(true);
      clone.classList.add("is-clone");

      track.insertBefore(clone, slides[0]);

      track.style.scrollBehavior = "auto";
      const slideWidth = clone.getBoundingClientRect().width;
      const gap = parseFloat(window.getComputedStyle(track).gap) || 0;
      const offset = slideWidth + gap;
      track.scrollLeft += offset;
      track.style.scrollBehavior = "smooth";

      track.scrollTo({
        left: 0,
        behavior: "smooth"
      });

      this.activeSlideIndex = slideCount - 1;
      this.updateControlsState();

      setTimeout(() => {
        track.style.scrollBehavior = "auto";
        track.scrollLeft = lastSlide.offsetLeft - track.offsetLeft;
        track.style.scrollBehavior = "smooth";
        clone.remove();
      }, 500);
      return;
    }

    const slide = slides[index];
    if (!slide) return;

    const targetScrollLeft = slide.offsetLeft - track.offsetLeft;
    track.scrollTo({
      left: targetScrollLeft,
      behavior: "smooth"
    });

    this.activeSlideIndex = index;
    this.updateControlsState();
  }

  /**
   * Update active slide indicators, dots, and arrow disabled states
   */
  updateControlsState() {
    const { prev, next } = this.refs;
    const isLoop = this.getAttribute("data-slider-loop") === "true";

    // Update slides is-active class
    this.slides.forEach((slide, index) => {
      if (index === this.activeSlideIndex) {
        slide.classList.add("is-active");
      } else {
        slide.classList.remove("is-active");
      }
    });

    // Update dots active state
    this.dots.forEach((dot, index) => {
      if (index === this.activeSlideIndex) {
        dot.classList.add("brand-video-testimonials__dot--active");
        dot.setAttribute("aria-current", "true");
      } else {
        dot.classList.remove("brand-video-testimonials__dot--active");
        dot.removeAttribute("aria-current");
      }
    });

    // Update arrows enabled/disabled
    if (prev) {
      prev.disabled = !isLoop && this.activeSlideIndex === 0;
    }
    if (next) {
      next.disabled = !isLoop && this.activeSlideIndex === this.slides.length - 1;
    }
  }

  setupAutoplayEvents() {
    this.addEventListener("mouseenter", () => this.stopAutoplay());
    this.addEventListener("mouseleave", () => this.startAutoplay());
    this.addEventListener("focusin", () => this.stopAutoplay());
    this.addEventListener("focusout", () => this.startAutoplay());

    this.addEventListener("touchstart", () => this.stopAutoplay(), { passive: true });
    this.addEventListener("touchend", () => {
      setTimeout(() => this.startAutoplay(), 1000);
    }, { passive: true });
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.getAttribute("data-slider-autoplay") !== "true") return;

    const delay = parseInt(this.getAttribute("data-slider-autoplay-delay") || "5") * 1000;
    this.autoplayTimer = setInterval(() => {
      if (this.isDragging) return;
      const slides = this.slides;
      const slideCount = slides.length;
      if (slideCount === 0) return;

      let nextIndex = this.activeSlideIndex + 1;
      const isLoop = this.getAttribute("data-slider-loop") === "true";
      if (nextIndex >= slideCount) {
        if (isLoop) {
          nextIndex = slideCount;
        } else {
          nextIndex = 0;
        }
      }
      this.scrollToSlide(nextIndex);
    }, delay);
  }

  stopAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  /**
   * Setup mouse drag-to-scroll for desktop carousel
   */
  setupDragging() {
    const { track } = this.refs;
    if (!track) return;

    const dragStart = (e) => {
      // Avoid dragging on click of buttons, product bar, or anchors
      if (e.target.closest("button") || e.target.closest("a") || e.target.closest(".brand-video-testimonials__product-bar")) {
        return;
      }
      this.isDragging = true;
      track.classList.add("is-dragging");
      this.startX = e.pageX - track.offsetLeft;
      this.scrollLeftStart = track.scrollLeft;
      track.style.scrollSnapType = "none"; // Disable snapping temporarily for free drag
      track.style.scrollBehavior = "auto";
    };

    const dragMove = (e) => {
      if (!this.isDragging) return;
      e.preventDefault();
      const x = e.pageX - track.offsetLeft;
      const walk = (x - this.startX) * 1.5; // Drag sensitivity
      track.scrollLeft = this.scrollLeftStart - walk;
    };

    const dragEnd = () => {
      if (!this.isDragging) return;
      this.isDragging = false;
      track.classList.remove("is-dragging");
      track.style.scrollSnapType = "x mandatory";
      track.style.scrollBehavior = "smooth";

      // Let scroll snap calculate final snap position, then trigger sync
      this.handleScroll();
    };

    // Desktop mouse drag
    track.addEventListener("mousedown", dragStart);
    track.addEventListener("mousemove", dragMove);
    window.addEventListener("mouseup", dragEnd);
  }

  /**
   * Setup card-level controls (mute/unmute floating buttons and card click)
   */
  setupCardControls() {
    this.slides.forEach((slide) => {
      const card = slide.querySelector(".brand-video-testimonial-card");
      if (!card) return;

      // Mute button click
      const muteBtn = card.querySelector(".brand-video-testimonial-card__mute-btn");
      if (muteBtn) {
        muteBtn.addEventListener("click", (e) => {
          e.stopPropagation();
          this.toggleMuteState();
        });
      }

      // Clicking on card itself toggles play/pause (excluding buttons/links)
      card.addEventListener("click", (e) => {
        if (e.target.closest("button") || e.target.closest("a") || e.target.closest(".brand-video-testimonials__product-bar")) {
          return;
        }
        const dm = card.querySelector("deferred-media");
        if (dm) {
          dm.toggleMedia?.();
        }
      });
    });
  }

  /**
   * Toggle global mute state and synchronize all active videos
   */
  toggleMuteState() {
    BrandVideoTestimonials.isGlobalMuted = !BrandVideoTestimonials.isGlobalMuted;

    // Update all loaded video elements
    this.slides.forEach((slide) => {
      const video = slide.querySelector("video");
      if (video) {
        video.muted = BrandVideoTestimonials.isGlobalMuted;
      }
    });

    this.updateCardMuteButtons();
  }

  /**
   * Update SVG icons of all mute buttons in this section to match the global mute state
   */
  updateCardMuteButtons() {
    this.slides.forEach((slide) => {
      const muteBtn = slide.querySelector(".brand-video-testimonial-card__mute-btn");
      if (!muteBtn) return;

      const muteIcon = muteBtn.querySelector(".icon-mute");
      const unmuteIcon = muteBtn.querySelector(".icon-unmute");

      if (BrandVideoTestimonials.isGlobalMuted) {
        muteIcon?.classList.remove("hidden");
        unmuteIcon?.classList.add("hidden");
      } else {
        muteIcon?.classList.add("hidden");
        unmuteIcon?.classList.remove("hidden");
      }
    });
  }
}

if (!customElements.get("brand-video-testimonials")) {
  customElements.define("brand-video-testimonials", BrandVideoTestimonials);
}
export { BrandVideoTestimonials };
